import type { ReactNode } from 'react';
import React, { createContext, useContext, useCallback, useState } from 'react';
import { useNavigate } from '@/shared/navigation';

export type TabName = 'Home' | 'Stats' | 'Notifications' | 'User';
// 각 탭의 기본 경로
export const TAB_PATHS: Record<TabName, string> = {
    Home: '/boards',
    Stats: '/stats',
    Notifications: '/notifications',
    User: '/user'
  };

export interface TabHandlers {
  Home?: () => void;
  Stats?: () => void;
  Notifications?: () => void;
  User?: () => void;
}

interface BottomTabHandlerContextType {
  registerTabHandler: (tabName: TabName, handler: () => void) => void;
  unregisterTabHandler: (tabName: TabName) => void;
  handleTabAction: (tabName: TabName) => void;
  hasRegisteredHandler: (tabName: TabName) => boolean;
}

const BottomTabHandlerContext = createContext<BottomTabHandlerContextType | undefined>(undefined);

export const BottomTabHandlerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [handlers, setHandlers] = useState<TabHandlers>({});
  const navigate = useNavigate();

  const createDefaultHandlers = useCallback((tab: TabName) => {
    return () => {
      navigate(TAB_PATHS[tab]);
    }
  }, [navigate]);

  const registerTabHandler = useCallback((tabName: TabName, handler: () => void) => {
    setHandlers(prev => ({ ...prev, [tabName]: handler }));
  }, []);

  const unregisterTabHandler = useCallback((tabName: TabName) => {
    setHandlers(prev => {
      const newHandlers = { ...prev };
      delete newHandlers[tabName];
      return newHandlers;
    });
  }, []);

  const handleTabAction = useCallback((tabName: TabName) => {
    const handler = handlers[tabName] || createDefaultHandlers(tabName);
    if (handler) {
      handler();
    }
  }, [handlers, createDefaultHandlers]);

  const hasRegisteredHandler = useCallback(
    (tabName: TabName) => Boolean(handlers[tabName]),
    [handlers],
  );

  return (
    <BottomTabHandlerContext.Provider value={{ registerTabHandler, unregisterTabHandler, handleTabAction, hasRegisteredHandler }}>
      {children}
    </BottomTabHandlerContext.Provider>
  );
};

export const useBottomTabHandler = () => {
  const context = useContext(BottomTabHandlerContext);
  if (context === undefined) {
    throw new Error('useBottomTabHandler must be used within a BottomTabHandlerProvider');
  }
  return context;
};

/**
 * Registers a bottom-tab action handler for the given tab.
 *
 * The latest handler is kept in a ref and invoked through a stable wrapper, so an
 * unstable (inline, non-memoized) handler identity does NOT re-run the registration
 * effect on every render. Registering on every render would push a new handler into
 * the context state, re-render every consumer, produce yet another handler, and loop
 * forever ("Maximum update depth exceeded").
 */
export const useRegisterTabHandler = (tabName: TabName, handler: () => void) => {
  const { registerTabHandler, unregisterTabHandler } = useBottomTabHandler();

  const handlerRef = React.useRef(handler);
  handlerRef.current = handler;

  React.useEffect(() => {
    const stableHandler = () => handlerRef.current();
    registerTabHandler(tabName, stableHandler);
    return () => unregisterTabHandler(tabName);
  }, [registerTabHandler, unregisterTabHandler, tabName]);
};